<template>
  <div>
    <p class="subtitle-2 mb-1" style="color: rgba(0,0,0,0.7)">Objetivo da campanha</p>
    <p class="caption grey--text mb-4">Selecione o tipo de produto ou indicador que será vendido</p>

    <v-item-group v-model="form.tipoConteudo" mandatory>
      <div class="d-flex" style="gap: 8px">
        <div v-for="opt in tiposConteudo" :key="opt.id" style="flex: 1; min-width: 0">
          <v-item :value="opt.id" v-slot="{ active, toggle }">
            <v-card
              outlined
              :color="active ? 'primary' : ''"
              :dark="active"
              @click="toggle"
              style="cursor: pointer; height: 100%"
            >
              <v-card-text class="text-center py-3 px-1">
                <v-icon :color="active ? 'white' : 'grey'" class="mb-1">{{ opt.icon }}</v-icon>
                <p class="caption ma-0 font-weight-medium" style="line-height: 1.2; word-break: break-word">
                  {{ opt.label }}
                </p>
              </v-card-text>
            </v-card>
          </v-item>
        </div>
      </div>
    </v-item-group>

    <!-- Indicador -->
    <div v-if="form.tipoConteudo === 1" class="mt-2">
      <div v-if="loadingIndicadores" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>
      <template v-else>
        <v-text-field
          v-model="filtroIndicador"
          placeholder="Filtrar indicadores..."
          dense
          outlined
          hide-details
          prepend-inner-icon="mdi-magnify"
          class="mb-3"
        ></v-text-field>
        <v-row>
          <v-col>
            <h4 class="mb-2">
              <v-icon class="mr-1" color="red">mdi-close</v-icon>Indicadores Disponíveis
            </h4>
            <v-btn
              class="mb-2"
              color="success"
              small
              block
              :disabled="indicadoresDisponiveis.length === 0"
              @click="adicionarTodosIndicadores"
            >Adicionar todos</v-btn>
            <draggable class="draggable-area-ind exclude-ind" :list="indicadoresDisponiveis" group="indicadores">
              <div
                class="v-list-item-ind__content list-ind"
                v-for="ind in indicadoresDisponiveis"
                :key="ind.id_indicador"
              >
                <div class="list-ind-items">
                  <div class="list-ind-col">
                    <span>{{ ind.indicador }}</span>
                    <span class="caption grey--text text--darken-1">{{ ind.id_indicador }}</span>
                  </div>
                  <div class="mr-1">
                    <v-btn icon small @click="adicionarIndicador(ind)">
                      <v-icon color="green">mdi-arrow-right-bold</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </draggable>
          </v-col>

          <v-divider vertical></v-divider>

          <v-col>
            <h4 class="mb-2">
              <v-icon class="mr-1" color="green">mdi-check</v-icon>Indicadores Selecionados
            </h4>
            <v-btn
              class="mb-2"
              color="error"
              small
              block
              :disabled="form.indicadoresSelecionados.length === 0"
              @click="removerTodosIndicadores"
            >Remover todos</v-btn>
            <draggable class="draggable-area-ind include-ind" :list="form.indicadoresSelecionados" group="indicadores">
              <div
                class="v-list-item-ind__content"
                v-for="ind in form.indicadoresSelecionados"
                :key="ind.id_indicador"
              >
                <div class="list-ind-participantes-items">
                  <div>
                    <v-btn class="mr-2" icon small @click="removerIndicador(ind)">
                      <v-icon color="red">mdi-arrow-left-bold</v-icon>
                    </v-btn>
                  </div>
                  <div class="list-ind-col mr-4">
                    <span>{{ ind.indicador }}</span>
                    <span class="caption grey--text text--darken-1">{{ ind.id_indicador }}</span>
                  </div>
                </div>
              </div>
            </draggable>
            <v-alert
              v-if="form.indicadoresSelecionados.length > 1"
              type="warning"
              dense
              text
              class="mt-2 caption mb-0"
            >
              <strong>Indicadores atrelados:</strong> com mais de um indicador, o bônus só é pago quando
              <strong>todos</strong> atingirem suas metas individualmente.
            </v-alert>
          </v-col>
        </v-row>
      </template>
    </div>

    <!-- Chassi -->
    <div v-if="form.tipoConteudo === 2" class="mt-2">
      <v-text-field
        v-model="searchChassi"
        placeholder="Buscar por chassi completo..."
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        :loading="loadingChassi"
        hide-details
        class="mb-3"
      ></v-text-field>
      <v-row>
        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="red">mdi-close</v-icon>Chassis Disponíveis
          </h4>
          <v-btn
            class="mb-2"
            color="success"
            small
            block
            :disabled="chassiResultados.length === 0"
            @click="adicionarTodosChassisDisponiveis"
          >Adicionar todos</v-btn>
          <div
            v-if="loadingChassi && chassiResultados.length === 0"
            class="d-flex justify-center py-4 draggable-area-ind exclude-ind"
          >
            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
          </div>
          <draggable v-else class="draggable-area-ind exclude-ind" :list="chassiResultados" group="chassis">
            <div
              class="v-list-item-ind__content list-ind"
              v-for="chassi in chassiResultados"
              :key="chassiKey(chassi)"
            >
              <div class="list-ind-items">
                <div class="list-ind-col">
                  <span class="body-2 font-weight-medium">
                    {{ chassi.chassi_completo }}
                    <span class="grey--text px-1">·</span>
                    <span class="primary--text">{{ chassi.chassi_resumido }}</span>
                  </span>
                  <span class="caption grey--text text--darken-1">{{ chassi.descricao_modelo }} · {{ chassi.apelido }}</span>
                  <span class="caption d-flex align-center mt-1" style="gap: 6px">
                    <v-chip x-small label outlined :color="statusChassi(chassi).color">{{ statusChassi(chassi).label }}</v-chip>
                    <span v-if="dataVendaChassi(chassi)" class="grey--text text--darken-1">vendido em {{ formatarData(dataVendaChassi(chassi)) }}</span>
                  </span>
                </div>
                <div class="mr-1">
                  <v-btn icon small @click="adicionarChassi(chassi)">
                    <v-icon color="green">mdi-arrow-right-bold</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <p v-if="chassiResultados.length === 0 && !searchChassi" class="caption grey--text text-center py-4 ma-0">
              Digite o chassi para buscar
            </p>
            <p v-if="chassiResultados.length === 0 && searchChassi && !loadingChassi" class="caption grey--text text-center py-4 ma-0">
              Nenhum chassi encontrado
            </p>
          </draggable>
        </v-col>

        <v-divider vertical></v-divider>

        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="green">mdi-check</v-icon>Chassis Selecionados
          </h4>
          <v-btn
            class="mb-2"
            color="error"
            small
            block
            :disabled="form.chassisSelecionados.length === 0"
            @click="removerTodosChassisDisponiveis"
          >Remover todos</v-btn>
          <draggable class="draggable-area-ind include-ind" :list="form.chassisSelecionados" group="chassis">
            <div
              class="v-list-item-ind__content"
              v-for="chassi in form.chassisSelecionados"
              :key="chassiKey(chassi)"
            >
              <div class="list-ind-participantes-items">
                <div>
                  <v-btn class="mr-2" icon small @click="removerChassi(chassi)">
                    <v-icon color="red">mdi-arrow-left-bold</v-icon>
                  </v-btn>
                </div>
                <div class="list-ind-col mr-4">
                  <span class="body-2 font-weight-medium">
                    {{ chassi.chassi_completo }}
                    <span class="grey--text px-1">·</span>
                    <span class="primary--text">{{ chassi.chassi_resumido }}</span>
                  </span>
                  <span class="caption grey--text text--darken-1">
                    {{ chassi.descricao_modelo }}
                    <template v-if="dataVendaChassi(chassi)"> · vendido em {{ formatarData(dataVendaChassi(chassi)) }}</template>
                  </span>
                </div>
              </div>
            </div>
            <p v-if="form.chassisSelecionados.length === 0" class="caption grey--text text-center py-4 ma-0">
              Arraste ou clique → para adicionar
            </p>
          </draggable>
        </v-col>
      </v-row>
    </div>

    <!-- Modelo -->
    <div v-if="form.tipoConteudo === 3" class="mt-2">
      <div class="mb-3" style="border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; padding: 10px 12px; background: rgba(0,0,0,0.015)">
        <p class="caption font-weight-bold text-uppercase ma-0 mb-2" style="letter-spacing: 0.05em; font-size: 10px; color: rgba(0,0,0,0.7)">Fonte dos modelos</p>
        <div class="d-flex" style="gap: 6px">
          <button
            v-for="opt in origensConteudo"
            :key="opt.value"
            type="button"
            @click="setOrigemConteudo(opt.value)"
            class="rounded pa-2 text-center caption font-weight-medium flex-grow-1"
            style="cursor: pointer; border: 2px solid; transition: all 0.2s; font-family: inherit; outline: none; -webkit-appearance: none; appearance: none"
            :style="form.origemConteudo === opt.value ? 'border-color: #1976d2; background: rgba(25,118,210,0.06); color: #1976d2' : 'border-color: rgba(0,0,0,0.12); background: white; color: rgba(0,0,0,0.6)'"
          >{{ opt.label }}</button>
        </div>
        <p class="caption grey--text ma-0 mt-2" style="line-height: 1.4">
          {{ form.origemConteudo === 2
            ? "Conta cotas de consórcio (status P1) do modelo, não vendas de veículo."
            : "Conta vendas de veículo do modelo." }}
        </p>
      </div>
      <v-text-field
        v-model="searchModelo"
        placeholder="Buscar por código ou descrição..."
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        :loading="loadingModelo"
        hide-details
        class="mb-3"
      ></v-text-field>
      <v-row>
        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="red">mdi-close</v-icon>Modelos Disponíveis
          </h4>
          <v-btn
            class="mb-2"
            color="success"
            small
            block
            :disabled="modeloResultados.length === 0"
            @click="adicionarTodosModelos"
          >Adicionar todos</v-btn>
          <div
            v-if="loadingModelo && modeloResultados.length === 0"
            class="d-flex justify-center py-4 draggable-area-ind exclude-ind"
          >
            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
          </div>
          <draggable v-else class="draggable-area-ind exclude-ind" :list="modeloResultados" group="modelos">
            <div
              class="v-list-item-ind__content list-ind"
              v-for="modelo in modeloResultados"
              :key="modelo.id_produto"
            >
              <div class="list-ind-items">
                <div class="list-ind-col">
                  <span class="body-2 font-weight-medium">{{ nomeModelo(modelo) }}</span>
                  <span class="caption grey--text text--darken-1">ID {{ modelo.id_produto }}</span>
                </div>
                <div class="mr-1">
                  <v-btn icon small @click="adicionarModelo(modelo)">
                    <v-icon color="green">mdi-arrow-right-bold</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <p v-if="modeloResultados.length === 0 && !loadingModelo" class="caption grey--text text-center py-4 ma-0">
              Nenhum modelo disponível
            </p>
          </draggable>
        </v-col>

        <v-divider vertical></v-divider>

        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="green">mdi-check</v-icon>Modelos Selecionados
          </h4>
          <v-btn
            class="mb-2"
            color="error"
            small
            block
            :disabled="form.modelosSelecionados.length === 0"
            @click="removerTodosModelos"
          >Remover todos</v-btn>
          <draggable
            class="draggable-area-ind include-ind"
            :list="form.modelosSelecionados"
            group="modelos"
            @change="sincronizarArrastoModelo"
          >
            <div
              class="v-list-item-ind__content"
              v-for="modelo in form.modelosSelecionados"
              :key="modelo.id_produto"
            >
              <div class="list-ind-participantes-items">
                <div>
                  <v-btn class="mr-2" icon small @click="removerModelo(modelo)">
                    <v-icon color="red">mdi-arrow-left-bold</v-icon>
                  </v-btn>
                </div>
                <div class="list-ind-col mr-4">
                  <span class="body-2 font-weight-medium">{{ nomeModelo(modelo) }}</span>
                  <span class="caption grey--text text--darken-1">ID {{ modelo.id_produto }}</span>
                </div>
              </div>
            </div>
            <p v-if="form.modelosSelecionados.length === 0" class="caption grey--text text-center py-4 ma-0">
              Arraste ou clique → para adicionar
            </p>
          </draggable>
        </v-col>
      </v-row>
    </div>

    <!-- Peças e Acessórios -->
    <div v-if="form.tipoConteudo === 4" class="mt-2">
      <v-text-field
        v-model="searchPeca"
        placeholder="Buscar por código ou descrição..."
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        :loading="loadingPeca"
        hide-details
        class="mb-3"
      ></v-text-field>
      <v-row>
        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="red">mdi-close</v-icon>Peças e Acessórios Disponíveis
          </h4>
          <v-btn
            class="mb-2"
            color="success"
            small
            block
            :disabled="pecaResultados.length === 0"
            @click="adicionarTodasPecas"
          >Adicionar todos</v-btn>
          <div
            v-if="loadingPeca && pecaResultados.length === 0"
            class="d-flex justify-center py-4 draggable-area-ind exclude-ind"
          >
            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
          </div>
          <draggable v-else class="draggable-area-ind exclude-ind" :list="pecaResultados" group="pecas">
            <div
              class="v-list-item-ind__content list-ind"
              v-for="peca in pecaResultados"
              :key="peca.id_peca"
            >
              <div class="list-ind-items">
                <div class="list-ind-col">
                  <span class="body-2 font-weight-medium">{{ peca.id_peca }} — {{ peca.peca_acessorio }}</span>
                </div>
                <div class="mr-1">
                  <v-btn icon small @click="adicionarPeca(peca)">
                    <v-icon color="green">mdi-arrow-right-bold</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <p v-if="pecaResultados.length === 0 && !loadingPeca" class="caption grey--text text-center py-4 ma-0">
              Nenhuma peça e acessório disponível
            </p>
          </draggable>
        </v-col>

        <v-divider vertical></v-divider>

        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="green">mdi-check</v-icon>Peças e Acessórios Selecionados
          </h4>
          <v-btn
            class="mb-2"
            color="error"
            small
            block
            :disabled="form.modelosSelecionados.length === 0"
            @click="removerTodasPecas"
          >Remover todos</v-btn>
          <draggable class="draggable-area-ind include-ind" :list="form.modelosSelecionados" group="pecas">
            <div
              class="v-list-item-ind__content"
              v-for="peca in form.modelosSelecionados"
              :key="peca.id_peca"
            >
              <div class="list-ind-participantes-items">
                <div>
                  <v-btn class="mr-2" icon small @click="removerPeca(peca)">
                    <v-icon color="red">mdi-arrow-left-bold</v-icon>
                  </v-btn>
                </div>
                <div class="list-ind-col mr-4">
                  <span class="body-2 font-weight-medium">{{ peca.id_peca }} — {{ peca.peca_acessorio }}</span>
                </div>
              </div>
            </div>
            <p v-if="form.modelosSelecionados.length === 0" class="caption grey--text text-center py-4 ma-0">
              Arraste ou clique → para adicionar
            </p>
          </draggable>
        </v-col>
      </v-row>
    </div>

    <!-- Serviços -->
    <div v-if="form.tipoConteudo === 5" class="mt-2">
      <v-text-field
        v-model="searchServico"
        placeholder="Buscar por código ou descrição..."
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        :loading="loadingServico"
        hide-details
        class="mb-3"
      ></v-text-field>
      <v-row>
        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="red">mdi-close</v-icon>Serviços Disponíveis
          </h4>
          <v-btn
            class="mb-2"
            color="success"
            small
            block
            :disabled="servicoResultados.length === 0"
            @click="adicionarTodosServicos"
          >Adicionar todos</v-btn>
          <div
            v-if="loadingServico && servicoResultados.length === 0"
            class="d-flex justify-center py-4 draggable-area-ind exclude-ind"
          >
            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
          </div>
          <draggable v-else class="draggable-area-ind exclude-ind" :list="servicoResultados" group="servicos">
            <div
              class="v-list-item-ind__content list-ind"
              v-for="servico in servicoResultados"
              :key="servico.id_servico"
            >
              <div class="list-ind-items">
                <div class="list-ind-col">
                  <span class="body-2 font-weight-medium">{{ servico.id_servico }} — {{ servico.servico }}</span>
                </div>
                <div class="mr-1">
                  <v-btn icon small @click="adicionarServico(servico)">
                    <v-icon color="green">mdi-arrow-right-bold</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <p v-if="servicoResultados.length === 0 && !loadingServico" class="caption grey--text text-center py-4 ma-0">
              Nenhum serviço disponível
            </p>
          </draggable>
        </v-col>

        <v-divider vertical></v-divider>

        <v-col>
          <h4 class="mb-2">
            <v-icon class="mr-1" color="green">mdi-check</v-icon>Serviços Selecionados
          </h4>
          <v-btn
            class="mb-2"
            color="error"
            small
            block
            :disabled="form.modelosSelecionados.length === 0"
            @click="removerTodosServicos"
          >Remover todos</v-btn>
          <draggable class="draggable-area-ind include-ind" :list="form.modelosSelecionados" group="servicos">
            <div
              class="v-list-item-ind__content"
              v-for="servico in form.modelosSelecionados"
              :key="servico.id_servico"
            >
              <div class="list-ind-participantes-items">
                <div>
                  <v-btn class="mr-2" icon small @click="removerServico(servico)">
                    <v-icon color="red">mdi-arrow-left-bold</v-icon>
                  </v-btn>
                </div>
                <div class="list-ind-col mr-4">
                  <span class="body-2 font-weight-medium">{{ servico.id_servico }} — {{ servico.servico }}</span>
                </div>
              </div>
            </div>
            <p v-if="form.modelosSelecionados.length === 0" class="caption grey--text text-center py-4 ma-0">
              Arraste ou clique → para adicionar
            </p>
          </draggable>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import campanhaService from "@/services/campanha";
import serviceParametros from "@/services/parametros";
import http, { getApiError } from "@/services/http";
import { getModelosConsorcio } from "@/services/consorcio";
import { formatarNomeModelo } from "./nomeModeloUtils";

// Concatena mantendo a primeira ocorrencia de cada chave.
//
// Os "Adicionar todos" concatenavam direto. Item repetido na lista de
// selecionados vira uma linha por (item, participante) no payload, e o banco
// aceita — mcp_meta_conteudo_modelo_meta nao tem unicidade. O sintoma nao
// aparece nas linhas de item da apuracao, so no realizado total da linha 'C',
// que soma o item duas vezes.
// A chave e normalizada com String porque os ids chegam de endpoints
// diferentes: o selecionado vem de metas-campanha, o disponivel do catalogo. Um
// devolvendo 174 e o outro "174" faria o Set tratar como itens distintos e a
// deduplicacao nao pegaria nada.
const chaveId = v => (v === null || v === undefined ? "" : String(v));

// Comparacao de id entre listas de origens diferentes. Id ausente nunca casa —
// duas linhas sem id nao sao "o mesmo item", e tratar como iguais esconderia
// item no lado de disponiveis.
const mesmoId = (a, b) =>
  a !== null && a !== undefined &&
  b !== null && b !== undefined &&
  String(a) === String(b);

const concatUnico = (atual, novos, chave) => {
  const vistos = new Set(atual.map(a => chaveId(chave(a))));
  return [
    ...atual,
    ...novos.filter(n => {
      const k = chaveId(chave(n));
      if (vistos.has(k)) return false;
      vistos.add(k);
      return true;
    })
  ];
};

export default {
  name: "ModalCadastroSubEtapaObjetivo",

  components: { draggable },

  props: {
    form: { type: Object, required: true },
    campanha: { type: Object, default: () => ({}) },
    editando: { type: Boolean, default: false }
  },

  data() {
    return {
      filtroIndicador: "",
      indicadoresDisponiveis: [],
      indicadoresTodosDisponiveis: [],
      loadingIndicadores: false,
      searchChassi: "",
      searchChassiTimeout: null,
      chassiResultados: [],
      loadingChassi: false,
      searchModelo: "",
      modeloResultados: [],
      modelosTodosDisponiveis: [],
      loadingModelo: false,
      searchPeca: "",
      searchPecaTimeout: null,
      pecaResultados: [],
      loadingPeca: false,
      searchServico: "",
      searchServicoTimeout: null,
      servicoResultados: [],
      loadingServico: false,
      tiposConteudo: [
        { id: 1, label: "Indicador", icon: "mdi-chart-bar" },
        { id: 2, label: "Chassi", icon: "mdi-car-cog" },
        { id: 3, label: "Modelo", icon: "mdi-car" },
        { id: 4, label: "Peças e Acessórios", icon: "mdi-package-variant" },
        { id: 5, label: "Serviços", icon: "mdi-wrench" }
      ],
      origensConteudo: [
        { value: 1, label: "Veículo" },
        { value: 2, label: "Cota de consórcio" }
      ]
    };
  },

  computed: {},

  watch: {
    filtroIndicador(val) {
      const termo = (val || "").toLowerCase().trim();
      if (!termo) {
        this.indicadoresDisponiveis = [...this.indicadoresTodosDisponiveis];
      } else {
        this.indicadoresDisponiveis = this.indicadoresTodosDisponiveis.filter(
          ind =>
            (ind.indicador && ind.indicador.toLowerCase().includes(termo)) ||
            (ind.id_indicador && String(ind.id_indicador).toLowerCase().includes(termo))
        );
      }
    },
    "form.tipoConteudo"(val) {
      if (this.editando) return;
      this.form.indicadoresSelecionados = [];
      this.indicadoresDisponiveis = [];
      this.indicadoresTodosDisponiveis = [];
      this.filtroIndicador = "";
      this.form.chassisSelecionados = [];
      this.chassiResultados = [];
      this.searchChassi = "";
      this.form.modelosSelecionados = [];
      this.modeloResultados = [];
      this.modelosTodosDisponiveis = [];
      this.searchModelo = "";
      this.pecaResultados = [];
      this.searchPeca = "";
      this.servicoResultados = [];
      this.searchServico = "";
      // Camada do participante (cotas de consorcio) e exclusiva do objetivo
      // Modelo: trocar de objetivo sem limpar deixava essa bagagem vazando
      // para Indicador/Chassi/Peca/Servico, sem nenhuma tela fora do fluxo
      // de Modelo pra desliga-la. Volta tudo ao default de data()/iniciarNovaMeta().
      this.form.origemConteudo = 1;
      this.form.usaMetaColaborador = false;
      this.form.origemMeta = 1;
      this.form.origemBonusColaborador = 1;
      this.form.mesesReferencia = [];
      this.form.percentualCrescimento = 15;
      this.form.valorMinimoMeta = 10;
      this.form.metasCalculadas = {};
      this.form.valorBonusColaborador = null;
      this.form.comissaoReferencia = "P2";
      this.form.comissaoPercentual = 50;
      if (val === 1) {
        this.carregarIndicadores();
      } else if (val === 2) {
        this.form.tipoPagamento = 2;
      } else if (val === 3) {
        this.carregarModelos();
      }
      // Peça (4) e Serviço (5): buscam só ao digitar (estratégia do chassi)
    },

    searchChassi(newVal) {
      if (this.searchChassiTimeout) clearTimeout(this.searchChassiTimeout);
      this.searchChassiTimeout = setTimeout(() => { this.buscarChassis(newVal); }, 600);
    },

    searchModelo(val) {
      const termo = (val || "").toLowerCase().trim();
      if (!termo) {
        this.modeloResultados = [...this.modelosTodosDisponiveis];
      } else {
        this.modeloResultados = this.modelosTodosDisponiveis.filter(
          m =>
            (m.descricao_modelo && m.descricao_modelo.toLowerCase().includes(termo)) ||
            (m.cod_modelo && String(m.cod_modelo).toLowerCase().includes(termo))
        );
      }
    },

    searchPeca() {
      if (this.searchPecaTimeout) clearTimeout(this.searchPecaTimeout);
      this.searchPecaTimeout = setTimeout(() => { this.carregarPecas(); }, 400);
    },

    searchServico() {
      if (this.searchServicoTimeout) clearTimeout(this.searchServicoTimeout);
      this.searchServicoTimeout = setTimeout(() => { this.carregarServicos(); }, 400);
    }
  },

  mounted() {
    const tipo = this.form.tipoConteudo;
    if (tipo === 1) this.carregarIndicadores();
    else if (tipo === 3) this.carregarModelos();
    // Peça (4) e Serviço (5): buscam só ao digitar (estratégia do chassi)
  },

  methods: {
    // Task 21: nome do modelo para exibicao — cod_modelo so existe no
    // catalogo carregado por carregarModelos (fluxo de criacao); ao reabrir
    // uma meta salva, form.modelosSelecionados vem so com descricao_modelo
    // (ModalCadastroMeta.editarMeta). Ver nomeModeloUtils para a regra completa.
    nomeModelo(m) {
      return formatarNomeModelo(m.cod_modelo, m.descricao_modelo);
    },

    // Indicadores
    async carregarIndicadores() {
      this.loadingIndicadores = true;
      this.indicadoresDisponiveis = [];
      this.indicadoresTodosDisponiveis = [];
      try {
        const response = await http.get("/sistemas/indicadores", {
          params: { ativo: "S", ativa_campanha: "S", per_page: -1 }
        });
        const selecionadosIds = new Set(this.form.indicadoresSelecionados.map(i => chaveId(i.id_indicador)));
        const lista = (response.data.data || []).filter(i => !selecionadosIds.has(chaveId(i.id_indicador)));
        this.indicadoresTodosDisponiveis = lista;
        this.indicadoresDisponiveis = [...lista];
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar indicadores") });
      } finally {
        this.loadingIndicadores = false;
      }
    },

    adicionarIndicador(ind) {
      const id = ind.id_indicador;
      this.indicadoresDisponiveis = this.indicadoresDisponiveis.filter(i => !mesmoId(i.id_indicador, id));
      this.indicadoresTodosDisponiveis = this.indicadoresTodosDisponiveis.filter(i => !mesmoId(i.id_indicador, id));
      this.form.indicadoresSelecionados.push(ind);
    },

    removerIndicador(ind) {
      const idx = this.form.indicadoresSelecionados.findIndex(i => mesmoId(i.id_indicador, ind.id_indicador));
      if (idx !== -1) this.form.indicadoresSelecionados.splice(idx, 1);
      this.indicadoresTodosDisponiveis.push(ind);
      this.indicadoresDisponiveis.push(ind);
    },

    adicionarTodosIndicadores() {
      const adicionados = [...this.indicadoresDisponiveis];
      const adicionadosIds = new Set(adicionados.map(i => chaveId(i.id_indicador)));
      this.form.indicadoresSelecionados = concatUnico(
        this.form.indicadoresSelecionados, adicionados, i => i.id_indicador
      );
      this.indicadoresDisponiveis = [];
      this.indicadoresTodosDisponiveis = this.indicadoresTodosDisponiveis.filter(
        i => !adicionadosIds.has(chaveId(i.id_indicador))
      );
    },

    removerTodosIndicadores() {
      const removidos = [...this.form.indicadoresSelecionados];
      this.indicadoresTodosDisponiveis = concatUnico(
        this.indicadoresTodosDisponiveis, removidos, i => i.id_indicador
      );
      this.indicadoresDisponiveis = concatUnico(
        this.indicadoresDisponiveis, removidos, i => i.id_indicador
      );
      this.form.indicadoresSelecionados = [];
    },

    // Chassi
    // Chave por VEICULO FISICO + entrada em estoque: o mesmo VIN (chassi_completo)
    // pode ter varios resumidos ao longo do tempo (recompra gera novo resumido),
    // entao distinguimos por completo + resumido.
    chassiKey(c) {
      return `${c.chassi_completo}-${c.chassi_resumido}`;
    },
    statusChassi(c) {
      const s = String(c.status || "").toUpperCase();
      if (s === "E") return { label: "Em estoque", color: "green" };
      if (s === "V") return { label: "Vendido", color: "red" };
      return { label: s || "—", color: "grey" };
    },
    formatarData(d) {
      if (!d) return "—";
      const [date] = String(d).split(" ");
      const [y, m, day] = date.split("-");
      return `${day}/${m}/${y}`;
    },
    // Data da venda: so aparece quando o veiculo esta vendido (status 'V').
    // Em estoque ('E') fica sem data. Depende da view expor `data_venda`.
    dataVendaChassi(c) {
      return String(c.status || "").toUpperCase() === "V" ? c.data_venda || null : null;
    },
    isChassiSelecionado(chassi) {
      return this.form.chassisSelecionados.some(
        c => this.chassiKey(c) === this.chassiKey(chassi)
      );
    },

    async buscarChassis(query) {
      if (!query) { this.chassiResultados = []; return; }
      this.loadingChassi = true;
      try {
        const { data } = await campanhaService.getVeiculosEstoque({
          chassi_completo: query,
          cod_band: this.campanha.id_band,
          per_page: 5
        });
        this.chassiResultados = (data.data || [])
          .filter(c => !this.isChassiSelecionado(c))
          .map(c => ({ ...c, valor_bonus: "" }));
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar chassis") });
      } finally {
        this.loadingChassi = false;
      }
    },

    adicionarChassi(chassi) {
      this.chassiResultados = this.chassiResultados.filter(
        c => this.chassiKey(c) !== this.chassiKey(chassi)
      );
      this.form.chassisSelecionados.push({ ...chassi, valor_bonus: "" });
    },

    removerChassi(chassi) {
      this.form.chassisSelecionados = this.form.chassisSelecionados.filter(
        c => this.chassiKey(c) !== this.chassiKey(chassi)
      );
      this.chassiResultados.push(chassi);
    },

    adicionarTodosChassisDisponiveis() {
      this.form.chassisSelecionados = concatUnico(
        this.form.chassisSelecionados, this.chassiResultados, c => this.chassiKey(c)
      );
      this.chassiResultados = [];
    },

    removerTodosChassisDisponiveis() {
      this.chassiResultados = concatUnico(
        this.chassiResultados, this.form.chassisSelecionados, c => this.chassiKey(c)
      );
      this.form.chassisSelecionados = [];
    },

    // Modelo
    isModeloSelecionado(modelo) {
      return this.form.modelosSelecionados.some(m => mesmoId(m.id_produto, modelo.id_produto));
    },

    setOrigemConteudo(val) {
      if (this.form.origemConteudo === val) return;
      this.$set(this.form, "origemConteudo", val);
      this.form.modelosSelecionados = [];
      this.searchModelo = "";
      this.carregarModelos();
    },

    async carregarModelos() {
      this.loadingModelo = true;
      this.modeloResultados = [];
      this.modelosTodosDisponiveis = [];
      try {
        const { data } = this.form.origemConteudo === 2
          ? await getModelosConsorcio({ id_band: this.campanha.id_band, ativo: "S" })
          : await serviceParametros.getProdutoModelo({
              per_page: -1,
              id_band: this.campanha.id_band,
              ativo: "S"
            });
        const lista = (data.data || [])
          .filter(m => !this.isModeloSelecionado(m))
          .map(m => ({ ...m, valor_bonus: "" }));
        this.modelosTodosDisponiveis = lista;
        this.modeloResultados = [...lista];
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar modelos") });
      } finally {
        this.loadingModelo = false;
      }
    },

    adicionarModelo(modelo) {
      this.modeloResultados = this.modeloResultados.filter(m => !mesmoId(m.id_produto, modelo.id_produto));
      this.modelosTodosDisponiveis = this.modelosTodosDisponiveis.filter(m => !mesmoId(m.id_produto, modelo.id_produto));
      this.form.modelosSelecionados.push({ ...modelo, valor_bonus: "" });
    },

    removerModelo(modelo) {
      this.form.modelosSelecionados = this.form.modelosSelecionados.filter(
        m => !mesmoId(m.id_produto, modelo.id_produto)
      );
      this.modelosTodosDisponiveis.push(modelo);
      this.modeloResultados.push(modelo);
    },

    adicionarTodosModelos() {
      const adicionados = [...this.modeloResultados];
      const ids = new Set(adicionados.map(m => chaveId(m.id_produto)));
      this.form.modelosSelecionados = concatUnico(
        this.form.modelosSelecionados, adicionados, m => m.id_produto
      );
      this.modeloResultados = [];
      this.modelosTodosDisponiveis = this.modelosTodosDisponiveis.filter(m => !ids.has(chaveId(m.id_produto)));
    },

    removerTodosModelos() {
      const removidos = [...this.form.modelosSelecionados];
      this.modeloResultados = concatUnico(this.modeloResultados, removidos, m => m.id_produto);
      this.modelosTodosDisponiveis = concatUnico(
        this.modelosTodosDisponiveis, removidos, m => m.id_produto
      );
      this.form.modelosSelecionados = [];
    },

    // O draggable escreve em modeloResultados e form.modelosSelecionados direto,
    // sem passar por adicionarModelo/removerModelo. modelosTodosDisponiveis e a
    // fonte que o watcher de searchModelo usa para reconstruir a lista de
    // disponiveis, entao precisa acompanhar o arrasto — senao a busca traz de
    // volta para "disponiveis" um modelo que ja esta selecionado, e ele entra
    // duas vezes no proximo "Adicionar todos".
    sincronizarArrastoModelo({ added, removed }) {
      if (added) {
        const id = added.element.id_produto;
        this.modelosTodosDisponiveis = this.modelosTodosDisponiveis.filter(
          m => !mesmoId(m.id_produto, id)
        );
      }
      if (removed) {
        this.modelosTodosDisponiveis = concatUnico(
          this.modelosTodosDisponiveis, [removed.element], m => m.id_produto
        );
      }
    },

    // Peças
    isPecaSelecionada(peca) {
      return this.form.modelosSelecionados.some(m => mesmoId(m.id_peca, peca.id_peca));
    },

    async carregarPecas() {
      if (!this.searchPeca) { this.pecaResultados = []; return; }
      this.loadingPeca = true;
      try {
        const { data } = await serviceParametros.getPecaAcessorio({
          peca_acessorio: this.searchPeca,
          per_page: 5
        });
        this.pecaResultados = (data.data || []).filter(m => !this.isPecaSelecionada(m));
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar peças e acessórios") });
      } finally {
        this.loadingPeca = false;
      }
    },

    adicionarPeca(peca) {
      this.pecaResultados = this.pecaResultados.filter(m => !mesmoId(m.id_peca, peca.id_peca));
      this.form.modelosSelecionados.push({ ...peca });
    },

    removerPeca(peca) {
      this.form.modelosSelecionados = this.form.modelosSelecionados.filter(
        m => !mesmoId(m.id_peca, peca.id_peca)
      );
      this.pecaResultados.push(peca);
    },

    adicionarTodasPecas() {
      this.form.modelosSelecionados = concatUnico(
        this.form.modelosSelecionados, this.pecaResultados, m => m.id_peca
      );
      this.pecaResultados = [];
    },

    removerTodasPecas() {
      this.pecaResultados = concatUnico(
        this.pecaResultados, this.form.modelosSelecionados, m => m.id_peca
      );
      this.form.modelosSelecionados = [];
    },

    // Serviços
    isServicoSelecionado(servico) {
      return this.form.modelosSelecionados.some(m => mesmoId(m.id_servico, servico.id_servico));
    },

    async carregarServicos() {
      if (!this.searchServico) { this.servicoResultados = []; return; }
      this.loadingServico = true;
      try {
        const { data } = await serviceParametros.getPosVendaServico({
          servico: this.searchServico,
          per_page: 5
        });
        this.servicoResultados = (data.data || []).filter(m => !this.isServicoSelecionado(m));
      } catch (e) {
        this.$notify({ type: "error", text: getApiError(e, "Erro ao buscar serviços") });
      } finally {
        this.loadingServico = false;
      }
    },

    adicionarServico(servico) {
      this.servicoResultados = this.servicoResultados.filter(
        m => !mesmoId(m.id_servico, servico.id_servico)
      );
      this.form.modelosSelecionados.push({ ...servico });
    },

    removerServico(servico) {
      this.form.modelosSelecionados = this.form.modelosSelecionados.filter(
        m => !mesmoId(m.id_servico, servico.id_servico)
      );
      this.servicoResultados.push(servico);
    },

    adicionarTodosServicos() {
      this.form.modelosSelecionados = concatUnico(
        this.form.modelosSelecionados, this.servicoResultados, m => m.id_servico
      );
      this.servicoResultados = [];
    },

    removerTodosServicos() {
      this.servicoResultados = concatUnico(
        this.servicoResultados, this.form.modelosSelecionados, m => m.id_servico
      );
      this.form.modelosSelecionados = [];
    }
  }
};
</script>

<style scoped>
.v-list-item-ind__content {
  padding-inline-start: 15px;
  border: 1px solid hsla(0, 0%, 31%, 0.199);
  border-radius: 10px;
  margin-bottom: 4px;
}

.draggable-area-ind {
  min-height: 150px;
  max-height: 250px;
  padding: 8px;
  overflow: auto;
}

.draggable-area-ind.include-ind {
  border: 2px dashed hsla(128, 100%, 34%, 0.39);
}

.draggable-area-ind.include-ind::-webkit-scrollbar-track { background-color: #adf3af; }
.draggable-area-ind.include-ind::-webkit-scrollbar { width: 6px; }
.draggable-area-ind.include-ind::-webkit-scrollbar-thumb { background: #2a772c; }

.draggable-area-ind.exclude-ind {
  border: 2px dashed hsla(347, 100%, 34%, 0.39);
}

.draggable-area-ind.exclude-ind::-webkit-scrollbar-track { background-color: #e7b8b8; }
.draggable-area-ind.exclude-ind::-webkit-scrollbar { width: 6px; }
.draggable-area-ind.exclude-ind::-webkit-scrollbar-thumb { background: #9b3232; }

.list-ind-items {
  display: flex !important;
  justify-content: space-between;
}

.list-ind-participantes-items {
  display: flex !important;
}

.list-ind-col {
  display: flex !important;
  flex-direction: column;
}
</style>
